/**
 * <variant-selector> - gère la sélection de variantes sur la PDP.
 *
 * Markup attendu :
 *   <div class="main_product_container" data-section-id="{{ section.id }}">
 *     <div class="product-info">
 *       ... prix avec class="product-info__price" ...
 *       <form>
 *         <variant-selector data-product-handle="{{ product.handle }}">
 *           <input type="hidden" name="id" value="{{ current_variant.id }}">
 *           <fieldset>
 *             <input type="radio" name="options[Color]" value="..."
 *                    data-option-index="0">
 *             ...
 *           </fieldset>
 *           <script type="application/json" data-variants>{{ product.variants | json }}</script>
 *         </variant-selector>
 *         <button class="product-form__submit">...</button>
 *       </form>
 *     </div>
 *   </div>
 *
 * À chaque change :
 *   1. Calcule la variante matchant les options cochées.
 *   2. Met à jour <input name="id">.
 *   3. Met à jour l'URL avec ?variant=ID (history.replaceState).
 *   4. Fetch la Section Rendering API → /products/{handle}?variant=ID&section_id=Y
 *   5. Remplace les nœuds listés dans SWAP_TARGETS par leur version re-rendue
 *      (prix, bouton submit, etc.).
 *   6. Dispatche `variant:change` (bubbles) → autres composants peuvent réagir.
 *
 * AbortController : annule un fetch en attente si l'utilisateur clique vite.
 */
if (!customElements.get("variant-selector")) {
class VariantSelector extends HTMLElement {
  /**
   * Sélecteurs des nœuds à remplacer après re-rendu serveur.
   * Étendre cette liste pour swap d'autres parties (ex. ".product_images").
   */
  static SWAP_TARGETS = [".product-info__price", ".product-form__submit", ".product_info_stock"];

  connectedCallback() {
    const dataEl = this.querySelector("script[type='application/json'][data-variants]");
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error("[variant-selector] variants JSON invalide", e);
      return;
    }

    this.idInput = this.querySelector('input[name="id"]');
    this.productHandle = this.dataset.productHandle;
    this.sectionRoot = this.closest("[data-section-id]");
    this.sectionId = this.sectionRoot?.dataset.sectionId;

    this.addEventListener("change", this.#handleChange);

    // Calcule la dispo contextuelle au load (la version Liquid n'utilise
    // que value.available global → JS la raffine selon les options actives).
    this.#updateAvailability();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @type {AbortController|null} */
  #abortController = null;

  #handleChange = (event) => {
    if (event.target.name?.startsWith("options[") !== true) return;

    const variant = this.#findMatchingVariant();
    if (!variant) return;

    // 1. Input caché du form (pour /cart/add.js)
    if (this.idInput) this.idInput.value = variant.id;

    // 2. URL — uniquement sur une vraie PDP (présence d'un [data-section-id]).
    //    Hors PDP (ex. <variant-selector> injecté dans le quick-add modal sur
    //    une page collection), on ne réécrit pas l'URL de la page courante.
    if (this.sectionId) this.#updateUrl(variant.id);

    // 3. Recalcul de la dispo contextuelle (classe .disabled sur les radios
    //    qui mènent à une combinaison sans variante en stock).
    this.#updateAvailability();

    // 4. Event public - d'autres composants peuvent écouter
    this.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: { variant },
        bubbles: true,
      }),
    );

    // 5. Section rendering API → swap des nœuds
    this.#fetchSectionAndSwap(variant.id);
  };

  /**
   * Pour chaque radio, vérifie s'il existe au moins une variante DISPONIBLE
   * qui matche la sélection courante avec ce radio comme nouvelle valeur à
   * sa position. Si non → ajoute la classe `disabled` (et aria-disabled).
   */
  #updateAvailability() {
    // Sélection courante par position.
    const current = [];
    this.querySelectorAll('input[type="radio"][name^="options["]:checked').forEach((input) => {
      const idx = parseInt(input.dataset.optionIndex, 10);
      if (Number.isFinite(idx)) current[idx] = input.value;
    });

    this.querySelectorAll('input[type="radio"][name^="options["]').forEach((input) => {
      const idx = parseInt(input.dataset.optionIndex, 10);
      if (!Number.isFinite(idx)) return;

      // Hypothèse : on remplace la position idx par la valeur de ce radio.
      const hypothesis = [...current];
      hypothesis[idx] = input.value;

      const hasAvailable = this.variants.some((v) => v.available && v.options.every((opt, i) => opt === hypothesis[i]));

      input.classList.toggle("disabled", !hasAvailable);
      if (hasAvailable) {
        input.removeAttribute("aria-disabled");
      } else {
        input.setAttribute("aria-disabled", "true");
      }
    });
  }

  #findMatchingVariant() {
    const selected = [];
    this.querySelectorAll('input[type="radio"][name^="options["]:checked').forEach((input) => {
      const idx = parseInt(input.dataset.optionIndex, 10);
      if (Number.isFinite(idx)) selected[idx] = input.value;
    });

    return this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
  }

  #updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    history.replaceState({}, "", url.toString());
  }

  async #fetchSectionAndSwap(variantId) {
    if (!this.sectionId || !this.productHandle) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const root = window.Shopify?.routes?.root || "/";
    const url = `${root}products/${this.productHandle}?variant=${variantId}&section_id=${this.sectionId}`;

    try {
      const res = await fetch(url, { signal: this.#abortController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      VariantSelector.SWAP_TARGETS.forEach((selector) => {
        const fresh = doc.querySelector(selector);
        const current = this.sectionRoot?.querySelector(selector);
        if (fresh && current) current.replaceWith(fresh);
      });
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error("[variant-selector] fetch section failed:", e);
    }
  }

  /** Variante actuellement sélectionnée (lecture publique). */
  get selectedVariant() {
    return this.#findMatchingVariant() || null;
  }
}

  customElements.define("variant-selector", VariantSelector);
}
